// GoalsTrackerPage.js (TRANSLATED)
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { FinanceProvider, FinanceContext } from "../contexts/FinanceContext";
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { useTranslation } from "react-i18next"; // Added import

import { FiEdit, FiCheck, FiCircle } from "react-icons/fi";

// =========================================================
// I. GLOBAL STYLES
// =========================================================
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    background: var(--primaryBackgroundColor);
    color: var(--fontPrimaryColor);
  }
`;

// =========================================================
// II. FINANCE TOTALS HELPER
// =========================================================
const useFinanceTotals = (history) => {
  const [totals, setTotals] = useState({ totalIncome: 0, totalExpenses: 0 });

  useEffect(() => {
    if (!history) return setTotals({ totalIncome: 0, totalExpenses: 0 });

    let income = 0,
      expenses = 0;

    history.forEach((entry) => {
      const amt = Number(entry.amount || 0);
      if (entry.type === "income") income += amt;
      else if (entry.type === "spending") expenses += amt;
    });

    setTotals({ totalIncome: income, totalExpenses: expenses });
  }, [history]);

  return totals;
};

// =========================================================
// III. MOCK PROVIDERS
// =========================================================
export const TasksContext = createContext();
export const HabitsContext = createContext();

const MockExternalProvider = ({ children }) => {
  const [tasks] = useState({ completedCount: 12 });
  const [habits] = useState({ checkIns: 25 });

  return (
    <TasksContext.Provider value={{ tasks }}>
      <HabitsContext.Provider value={{ habits }}>
        {children}
      </HabitsContext.Provider>
    </TasksContext.Provider>
  );
};

// =========================================================
// IV. SMART MESSAGES
// =========================================================
const useSmartMessage = () => {
  const { t } = useTranslation();

  const getSmartMessage = (goal) => {
    if (goal.status === "Completed")
      return { text: t("goals.completedMessage"), color: "#4caf50" };

    // Calculate percentage based on type
    let pct;
    if (goal.type === "Milestone") {
      const completed = (goal.milestones || []).filter(
        (m) => m.completed
      ).length;
      const total = (goal.milestones || []).length || 1;
      pct = Math.min(100, Math.round((completed / total) * 100));
    } else {
      pct =
        goal.targetValue && goal.targetValue > 0
          ? Math.min(
              100,
              Math.round((goal.currentValue / goal.targetValue) * 100)
            )
          : 0;
    }

    if (!goal.deadline)
      return {
        text: `${pct}% ${t("goals.completed")}${
          goal.type === "Milestone" ? ` (${t("goals.milestones")})` : ""
        }`,
        color: "#2196f3",
      };

    const start = new Date(goal.createdAt).getTime();
    const end = new Date(goal.deadline).getTime();
    const now = Date.now();

    if (now > end) return { text: t("goals.overdue"), color: "#f44336" };

    const totalDuration = end - start;
    const elapsed = now - start;
    const timeProgress =
      totalDuration > 0 ? (elapsed / totalDuration) * 100 : 0;

    if (pct >= 95)
      return { text: t("goals.approachingGoal"), color: "#ff9800" };
    if (pct > timeProgress + 5)
      return { text: t("goals.aheadOfSchedule"), color: "#4caf50" };
    if (pct < timeProgress - 10)
      return { text: t("goals.behindSchedule"), color: "#f44336" };

    return { text: t("goals.onTrack"), color: "#2196f3" };
  };

  return getSmartMessage;
};

// =========================================================
// V. GOALS CONTEXT
// =========================================================
export const GoalsContext = createContext();

const GoalsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { history } = useContext(FinanceContext);
  const { tasks } = useContext(TasksContext);
  const { habits } = useContext(HabitsContext);
  const { t } = useTranslation(); // Added translation hook

  const { totalIncome, totalExpenses } = useFinanceTotals(history || []);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  const goalsCollectionRef = () =>
    collection(db, "users", currentUser.uid, "goals");

  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(
      goalsCollectionRef(),
      (snap) => {
        const data = snap.docs.map((d) => {
          const goalData = d.data();
          const id = d.id;

          // Handle Milestone type goals
          if (goalData.type === "Milestone") {
            const milestones = goalData.milestones || [];
            const completedCount = milestones.filter((m) => m.completed).length;
            const totalCount = milestones.length;

            return {
              id,
              title: goalData.title || "",
              type: goalData.type || t("goals.typePersonal"),
              targetValue: totalCount,
              currentValue: completedCount,
              status: goalData.status || "Active",
              createdAt: goalData.createdAt || new Date().toISOString(),
              deadline: goalData.deadline || null,
              milestones: milestones,
              ...goalData,
            };
          }

          return {
            id,
            title: goalData.title || "",
            type: goalData.type || t("goals.typePersonal"),
            targetValue: Number(goalData.targetValue || 0),
            currentValue: Number(goalData.currentValue || 0),
            status: goalData.status || "Active",
            createdAt: goalData.createdAt || new Date().toISOString(),
            deadline: goalData.deadline || null,
            ...goalData,
          };
        });
        setGoals(data);
        setLoading(false);
      },
      (err) => {
        console.error(t("goals.errorListening"), err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [currentUser, t]);

  const addGoal = async (goalData) => {
    if (!currentUser) return;

    const isMilestone = goalData.type === "Milestone";
    const payload = {
      title: goalData.title || t("goals.untitledGoal"),
      type: goalData.type || t("goals.typePersonal"),
      targetValue: isMilestone
        ? (goalData.milestones || []).length
        : Number(goalData.targetValue || 0),
      currentValue: isMilestone ? 0 : Number(goalData.currentValue || 0),
      status: goalData.status || "Active",
      createdAt: goalData.createdAt || new Date().toISOString(),
      deadline: goalData.deadline || null,
      milestones: isMilestone ? goalData.milestones : [],
      createdAtServer: serverTimestamp(),
    };
    try {
      await addDoc(goalsCollectionRef(), payload);
    } catch (err) {
      console.error(t("goals.errorAddGoal"), err);
    }
  };

  const updateGoal = async (id, updates) => {
    if (!currentUser) return;
    const ref = doc(db, "users", currentUser.uid, "goals", id);
    const normalized = { ...updates };
    if (normalized.targetValue !== undefined)
      normalized.targetValue = Number(normalized.targetValue);
    if (normalized.currentValue !== undefined)
      normalized.currentValue = Number(normalized.currentValue);

    // Handle milestone updates
    if (normalized.milestones !== undefined) {
      const completedCount = (normalized.milestones || []).filter(
        (m) => m.completed
      ).length;
      normalized.currentValue = completedCount;
      normalized.targetValue = (normalized.milestones || []).length;
    }

    try {
      await updateDoc(ref, normalized);
    } catch (err) {
      console.error(t("goals.errorUpdateGoal"), err);
    }
  };

  const deleteGoal = async (id) => {
    if (!currentUser) return;
    const ref = doc(db, "users", currentUser.uid, "goals", id);
    try {
      await deleteDoc(ref);
    } catch (err) {
      console.error(t("goals.errorDeleteGoal"), err);
    }
  };

  const updateManualProgress = async (id, newValue) => {
    const nv = Number(newValue || 0);
    const g = goals.find((gg) => gg.id === id);
    if (!g) return;
    const newStatus = nv >= g.targetValue ? "Completed" : "Active";
    await updateGoal(id, { currentValue: nv, status: newStatus });
  };

  const toggleMilestone = async (goalId, milestoneIndex) => {
    const goal = goals.find((g) => g.id === goalId);
    if (!goal || goal.type !== "Milestone") return;

    const newMilestones = [...(goal.milestones || [])];
    const isMarkingComplete = !newMilestones[milestoneIndex].completed;

    if (isMarkingComplete) {
      // Allow completing any incomplete milestone
      newMilestones[milestoneIndex].completed = true;
    } else {
      // Only allow uncompleting the LAST completed milestone
      const completedIndices = newMilestones
        .map((m, idx) => (m.completed ? idx : -1))
        .filter((idx) => idx !== -1);

      const lastCompletedIndex =
        completedIndices.length > 0
          ? completedIndices[completedIndices.length - 1]
          : -1;

      if (milestoneIndex === lastCompletedIndex) {
        newMilestones[milestoneIndex].completed = false;
      } else {
        alert(t("goals.uncheckWarning"));
        return;
      }
    }

    const completedCount = newMilestones.filter((m) => m.completed).length;
    const newStatus =
      completedCount === newMilestones.length ? "Completed" : "Active";

    await updateGoal(goalId, {
      milestones: newMilestones,
      currentValue: completedCount,
      status: newStatus,
    });
  };

  // Auto-sync
  useEffect(() => {
    if (!currentUser || goals.length === 0) return;
    const saved = Math.max(
      0,
      Number(totalIncome || 0) - Number(totalExpenses || 0)
    );

    goals.forEach((g) => {
      if (
        !g ||
        g.type === "Personal" ||
        g.type === "Milestone" ||
        g.status === "Completed"
      )
        return;

      let computed = g.currentValue || 0;
      if (g.type === "Financial") computed = saved;
      else if (g.type === "Task")
        computed = Number((tasks && tasks.completedCount) || 0);
      else if (g.type === "Habit")
        computed = Number((habits && habits.checkIns) || 0);

      const newStatus = computed >= g.targetValue ? "Completed" : "Active";
      if (g.currentValue !== computed || g.status !== newStatus) {
        updateGoal(g.id, { currentValue: computed, status: newStatus }).catch(
          (err) => console.error(t("goals.errorAutoUpdate"), err)
        );
      }
    });
  }, [totalIncome, totalExpenses, tasks, habits, goals, currentUser, t]);

  return (
    <GoalsContext.Provider
      value={{
        goals,
        loading,
        addGoal,
        updateGoal,
        deleteGoal,
        updateManualProgress,
        toggleMilestone,
      }}
    >
      {children}
    </GoalsContext.Provider>
  );
};

// =========================================================
// VI. POPUP ANIMATION & STYLED COMPONENTS
// =========================================================
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;
const fadeOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.95); }
`;
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  backdrop-filter: blur(5px);
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
`;
const Modal = styled.div`
  transition: 0.5s;
  padding: 1.6rem;
  width: 500px;
  border-radius: 20px;
  background: var(--popupColor);
  transition: 0.5s;
  h2 {
    color: var(--fontPrimaryColor);
    text-align: center;
    font-size: 20px;
    margin: 1em 0;
  }
  @media (max-width: 600px) {
    width: 90%;
  }
`;
// FORM STYLES
const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--fontSecondaryColor);
  margin-top: 15px;
`;
const Input = styled.input`
  padding: 15px 20px;
  border: none;
  margin-top: 10px;
  border-radius: 6px;
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  margin-bottom: 10px;
  width: 100%;
  box-shadow: 0 0 10px 1px var(--popupShadow);
  outline: none;
  font-family: "Montserrat", sans-serif;
`;
const Select = styled.select`
  padding: 15px 20px;
  border-radius: 5px;
  border: none;
  font-size: 0.8rem;
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  margin-bottom: 10px;
  width: 100%;
  box-shadow: 0 0 10px 1px var(--popupShadow);
  border: none;
  font-family: "Montserrat", sans-serif;
  outline: none;
  cursor: pointer;
  margin-top: 10px;
`;
const SubmitButton = styled.button`
  background: var(--fontPrimaryColor);
  color: var(--thirdBackgroundColor);
  border: none;
  padding: 12px;
  margin-top: 20px;
  border-radius: 6px;
  font-family: "Montserrat", sans-serif;
  width: 100%;
  cursor: pointer;
  &:hover {
    background: #717d9b;
  }
`;
const AddButton = styled.button`
  margin-top: 2em;
  background: #495269;
  color: white;
  border: none;
  width: 100%;
  padding: 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: 0.2s;
  margin-bottom: 1em;
  &:hover {
    background: #717d9b;
  }
`;

// =========================================================
// VII. ROW GOALS (PERMANENTLY STACKED)
// =========================================================
const Row = styled.div`
  background: var(--thirdBackgroundColor);
  padding: 15px 20px;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 0.95rem;
  gap: 5px;
  color: var(--fontPrimaryColor);
  transition: 0.5s;

  .title-type-wrapper {
    max-width: 100%;
    width: 100%;
  }

  .title,
  .type {
    width: auto;
    text-align: left;
  }
`;
const ProgressNumber = styled.span`
  color: ${(p) => p.color};
  width: auto;
  text-align: left;
  flex-shrink: 0;
  font-weight: 600;
`;
const ProgressAndActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: space-between;
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px solid var(--fontThirdColor);
  flex-shrink: 0;
`;
const ActionButtons = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  flex-shrink: 0;
`;
const DeleteBtn = styled.button`
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 5px;
  &:hover {
    color: #f44336;
  }
`;
const EditButton = styled.button`
  background: transparent;
  border: none;
  color: var(--fontSecondaryColor);
  cursor: pointer;
  padding: 5px;
  &:hover {
    color: #2196f3;
  }
`;

// Milestone Steps Styles
const MilestoneSteps = styled.div`
  width: 100%;
  margin-top: 10px;
  padding: 10px 0 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const MilestoneStep = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  opacity: ${(props) => (props.completed ? 0.7 : 1)};
`;

const MilestoneCheckbox = styled.button`
  background: ${(props) => (props.completed ? "#4caf50" : "transparent")};
  border: 1px solid ${(props) => (props.completed ? "#4caf50" : "#666")};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    border-color: ${(props) => (props.completed ? "#45a049" : "#888")};
  }
`;

const MilestoneText = styled.span`
  font-size: 0.85rem;
  color: ${(props) => (props.completed ? "#aaa" : "var(--fontPrimaryColor)")};
  text-decoration: ${(props) => (props.completed ? "line-through" : "none")};
  flex: 1;
`;

// =========================================================
// VIII. POPUP FORMS
// =========================================================

// --- Updated AddGoalModal with Milestone support ---
const AddGoalModal = ({ onClose }) => {
  const { addGoal } = useContext(GoalsContext);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: "",
    type: "Financial",
    targetValue: "",
    deadline: "",
    milestones: [],
  });
  const [newMilestoneText, setNewMilestoneText] = useState("");

  const overlayRef = useRef();

  const handleTypeChange = (e) => {
    setFormData({
      ...formData,
      type: e.target.value,
      targetValue: e.target.value === "Milestone" ? "" : formData.targetValue,
    });
  };

  const handleAddMilestone = () => {
    if (newMilestoneText.trim() === "") return;
    const newMilestone = {
      title: newMilestoneText.trim(),
      completed: false,
      id: Date.now() + Math.random(),
    };
    setFormData({
      ...formData,
      milestones: [...formData.milestones, newMilestone],
    });
    setNewMilestoneText("");
  };

  const handleRemoveMilestone = (id) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.filter((m) => m.id !== id),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) return;

    const isMilestone = formData.type === "Milestone";

    if (isMilestone && formData.milestones.length === 0) {
      alert(t("goals.milestoneError"));
      return;
    }

    if (!isMilestone && !formData.targetValue) return;

    await addGoal({
      ...formData,
      targetValue: isMilestone
        ? formData.milestones.length
        : Number(formData.targetValue),
      currentValue: isMilestone ? 0 : 0,
      status: "Active",
      createdAt: new Date().toISOString(),
      deadline: formData.deadline || null,
      milestones: isMilestone ? formData.milestones : [],
    });
    onClose();
  };

  const handleClickOutside = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const isMilestoneType = formData.type === "Milestone";

  return (
    <Overlay ref={overlayRef} onClick={handleClickOutside}>
      <Modal>
        <h2>{t("goals.addNewGoal")}</h2>
        <form onSubmit={handleSubmit}>
          <Label>{t("goals.title")}</Label>
          <Input
            placeholder={t("goals.titlePlaceholder")}
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />

          <Label>{t("goals.type")}</Label>
          <Select value={formData.type} onChange={handleTypeChange}>
            <option value="Financial">{t("goals.typeFinancial")}</option>
            <option value="Personal">{t("goals.typePersonal")}</option>
            <option value="Milestone">{t("goals.typeMilestone")}</option>
          </Select>

          {/* Target Value is only needed for non-milestone goals */}
          {!isMilestoneType && (
            <>
              <Label>{t("goals.targetValue")}</Label>
              <Input
                placeholder="400"
                type="number"
                value={formData.targetValue}
                onChange={(e) =>
                  setFormData({ ...formData, targetValue: e.target.value })
                }
                required
              />
            </>
          )}

          <Label>{t("goals.deadline")}</Label>
          <Input
            type="date"
            value={formData.deadline}
            onChange={(e) =>
              setFormData({ ...formData, deadline: e.target.value })
            }
          />

          {/* Milestone Input Area */}
          {isMilestoneType && (
            <div style={{ marginTop: "15px" }}>
              <Label>
                {t("goals.roadmapSteps")} ({formData.milestones.length}{" "}
                {t("goals.defined")})
              </Label>
              <div
                style={{ display: "flex", gap: "10px", marginBottom: "15px" }}
              >
                <Input
                  type="text"
                  placeholder={t("goals.milestonePlaceholder")}
                  value={newMilestoneText}
                  onChange={(e) => setNewMilestoneText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddMilestone();
                    }
                  }}
                  style={{ marginTop: "5px" }}
                />
                <button
                  type="button"
                  onClick={handleAddMilestone}
                  style={{
                    background: "#4caf50",
                    color: "white",
                    border: "none",
                    padding: "10px 15px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    marginTop: "5px",
                  }}
                >
                  {t("goals.addStep")}
                </button>
              </div>
              {formData.milestones.length > 0 && (
                <div style={{ marginTop: "10px" }}>
                  {formData.milestones.map((m) => (
                    <div
                      key={m.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        marginBottom: "5px",
                        background: "var(--secondaryBackgroundColor)",
                        borderRadius: "6px",
                      }}
                    >
                      <span>{m.title}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveMilestone(m.id)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#f44336",
                          cursor: "pointer",
                        }}
                      >
                        {t("goals.remove")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <SubmitButton type="submit">{t("goals.addGoal")}</SubmitButton>
        </form>
      </Modal>
    </Overlay>
  );
};

// --- Updated EditGoalModal with Milestone support ---
const EditGoalModal = ({ goal, onClose }) => {
  const { updateGoal } = useContext(GoalsContext);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: goal.title,
    type: goal.type,
    targetValue: goal.targetValue || "",
    currentValue: goal.currentValue || "",
    deadline: goal.deadline || "",
    milestones: goal.milestones || [],
  });
  const [newMilestoneText, setNewMilestoneText] = useState("");

  const overlayRef = useRef();

  const handleTypeChange = (e) => {
    setFormData({
      ...formData,
      type: e.target.value,
      targetValue: e.target.value === "Milestone" ? "" : formData.targetValue,
      milestones: e.target.value === "Milestone" ? formData.milestones : [],
    });
  };

  const handleAddMilestone = () => {
    if (newMilestoneText.trim() === "") return;
    const newMilestone = {
      title: newMilestoneText.trim(),
      completed: false,
      id: Date.now() + Math.random(),
    };
    setFormData({
      ...formData,
      milestones: [...formData.milestones, newMilestone],
    });
    setNewMilestoneText("");
  };

  const handleRemoveMilestone = (id) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.filter((m) => m.id !== id),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) return;

    const isMilestone = formData.type === "Milestone";
    const targetValue = isMilestone
      ? formData.milestones.length
      : Number(formData.targetValue);

    if (!isMilestone && !targetValue) return;

    const updates = {
      title: formData.title,
      type: formData.type,
      targetValue: targetValue,
      deadline: formData.deadline || null,
      milestones: isMilestone ? formData.milestones : [],
    };

    if (formData.type === "Personal") {
      const newCurrentValue = Number(formData.currentValue);
      updates.currentValue = newCurrentValue;
      updates.status =
        newCurrentValue >= updates.targetValue ? "Completed" : "Active";
    } else if (isMilestone) {
      const completedCount = (formData.milestones || []).filter(
        (m) => m.completed
      ).length;
      updates.currentValue = completedCount;
      updates.status =
        completedCount === formData.milestones.length ? "Completed" : "Active";
    }

    await updateGoal(goal.id, updates);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClickOutside = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const isMilestoneType = formData.type === "Milestone";

  return (
    <Overlay ref={overlayRef} onClick={handleClickOutside}>
      <Modal>
        <h2>
          {t("goals.editGoal")}: {goal.title}
        </h2>

        <form onSubmit={handleSubmit}>
          <Label>{t("goals.title")}</Label>
          <Input
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />

          <Label>{t("goals.typeChangeWarning")}</Label>
          <Select name="type" value={formData.type} onChange={handleTypeChange}>
            <option value="Financial">{t("goals.typeFinancial")}</option>
            <option value="Personal">{t("goals.typePersonal")}</option>
            <option value="Milestone">{t("goals.typeMilestone")}</option>
          </Select>

          {/* Target Value is only needed for non-milestone goals */}
          {!isMilestoneType && (
            <>
              <Label>{t("goals.targetValue")}</Label>
              <Input
                name="targetValue"
                type="number"
                value={formData.targetValue}
                onChange={handleChange}
                required
              />
            </>
          )}

          {formData.type === "Personal" && (
            <>
              <Label>{t("goals.currentValue")}</Label>
              <Input
                name="currentValue"
                type="number"
                value={formData.currentValue}
                onChange={handleChange}
                max={formData.targetValue}
                required
              />
            </>
          )}

          <Label>{t("goals.deadline")}</Label>
          <Input
            name="deadline"
            type="date"
            value={formData.deadline}
            onChange={handleChange}
          />

          {/* Milestone Input Area */}
          {isMilestoneType && (
            <div style={{ marginTop: "15px" }}>
              <Label>
                {t("goals.roadmapSteps")} ({formData.milestones.length}{" "}
                {t("goals.defined")})
              </Label>
              <div
                style={{ display: "flex", gap: "10px", marginBottom: "15px" }}
              >
                <Input
                  type="text"
                  placeholder={t("goals.milestonePlaceholder")}
                  value={newMilestoneText}
                  onChange={(e) => setNewMilestoneText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddMilestone();
                    }
                  }}
                  style={{ marginTop: "5px" }}
                />
                <button
                  type="button"
                  onClick={handleAddMilestone}
                  style={{
                    background: "#4caf50",
                    color: "white",
                    border: "none",
                    padding: "10px 15px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    marginTop: "5px",
                  }}
                >
                  {t("goals.addStep")}
                </button>
              </div>
              {formData.milestones.length > 0 && (
                <div style={{ marginTop: "10px" }}>
                  {formData.milestones.map((m) => (
                    <div
                      key={m.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        marginBottom: "5px",
                        background: "var(--secondaryBackgroundColor)",
                        borderRadius: "6px",
                      }}
                    >
                      <span
                        style={{
                          textDecoration: m.completed ? "line-through" : "none",
                          opacity: m.completed ? 0.7 : 1,
                        }}
                      >
                        {m.title}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveMilestone(m.id)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#f44336",
                          cursor: "pointer",
                        }}
                      >
                        {t("goals.remove")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <SubmitButton type="submit">{t("goals.saveChanges")}</SubmitButton>
        </form>
      </Modal>
    </Overlay>
  );
};

// =========================================================
// IX. MAIN PAGE
// =========================================================
const PageContainer = styled.div`
  padding: 20px 40px;
  /* width: 32%; */
  background-color: var(--secondaryBackgroundColor);
  /* margin: 0 auto; */
  color: var(--fontPrimaryColor);
  border-radius: 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  /* width: 31%; */
  transition: 0.5s;
  min-height: 400px;
  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    /* width: 100%; */
    margin-top: 1em;
    margin-bottom: 1em;
    text-align: center;
  }
  height: stretch;
  max-height: 600px;
  @media (max-height: 1100px) {
    max-height: 500px;
  }
  @media (max-width: 1400px) {
    /* width: 34%; */
    h1 {
      margin-bottom: 1em;
    }
  }
  /* @media (max-width: 1050px) {
    width: 100%;
  }*/
  @media (max-width: 500px) {
    padding: 2em 1.2em;
  }
`;

const GoalsListWrapper = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
  margin-bottom: 10px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #717d9b;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background-color: var(--secondaryBackgroundColor);
  }
`;

const GoalTitleAndType = ({ title, type }) => {
  const Wrapper = styled.span`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 200px;
    flex-grow: 1;

    .title {
      font-weight: 600;
      font-size: 1rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .type {
      font-size: 0.8rem;
      color: var(--fontSecondaryColor);
    }
  `;
  return (
    <Wrapper className="title-type-wrapper">
      <span className="title">{title}</span>
      <span className="type">{type}</span>
    </Wrapper>
  );
};

const HomeGoalTracker = () => {
  const { goals, deleteGoal, toggleMilestone } = useContext(GoalsContext);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const { t } = useTranslation();
  const getSmartMessage = useSmartMessage();

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <h1>{t("goals.title")}</h1>

        {goals.length === 0 ? (
          <p>{t("goals.noGoals")}</p>
        ) : (
          <GoalsListWrapper>
            {goals.map((goal) => {
              const msg = getSmartMessage(goal);
              let pct, current, target;

              if (goal.type === "Milestone") {
                const completed = (goal.milestones || []).filter(
                  (m) => m.completed
                ).length;
                const total = (goal.milestones || []).length || 1;
                pct = Math.min(100, Math.round((completed / total) * 100));
                current = completed;
                target = total;
              } else {
                pct = Math.round((goal.currentValue / goal.targetValue) * 100);
                current = goal.currentValue;
                target = goal.targetValue;
              }

              return (
                <Row key={goal.id}>
                  <GoalTitleAndType title={goal.title} type={goal.type} />

                  <ProgressAndActionsWrapper>
                    <ProgressNumber color={msg.color}>
                      {pct}% ({current}/{target})
                    </ProgressNumber>

                    <ActionButtons>
                      <EditButton onClick={() => setEditGoal(goal)}>
                        <FiEdit size={16} />
                      </EditButton>
                      <DeleteBtn onClick={() => deleteGoal(goal.id)}>
                        ✖
                      </DeleteBtn>
                    </ActionButtons>
                  </ProgressAndActionsWrapper>

                  {/* Milestone Steps for Milestone type goals */}
                  {goal.type === "Milestone" &&
                    goal.milestones &&
                    goal.milestones.length > 0 && (
                      <MilestoneSteps>
                        {goal.milestones.slice(0, 3).map((milestone, index) => (
                          <MilestoneStep
                            key={index}
                            onClick={() => toggleMilestone(goal.id, index)}
                            completed={milestone.completed}
                          >
                            <MilestoneCheckbox completed={milestone.completed}>
                              {milestone.completed ? (
                                <FiCheck size={12} />
                              ) : (
                                <FiCircle size={12} />
                              )}
                            </MilestoneCheckbox>
                            <MilestoneText completed={milestone.completed}>
                              {milestone.title}
                            </MilestoneText>
                          </MilestoneStep>
                        ))}
                        {goal.milestones.length > 3 && (
                          <div
                            style={{
                              fontSize: "0.8rem",
                              color: "#aaa",
                              textAlign: "center",
                              padding: "5px",
                            }}
                          >
                            +{goal.milestones.length - 3} {t("goals.moreSteps")}
                          </div>
                        )}
                      </MilestoneSteps>
                    )}
                </Row>
              );
            })}
          </GoalsListWrapper>
        )}
        <AddButton onClick={() => setShowAddModal(true)}>
          {t("goals.addGoal")}
        </AddButton>
      </PageContainer>

      {showAddModal && <AddGoalModal onClose={() => setShowAddModal(false)} />}

      {editGoal && (
        <EditGoalModal goal={editGoal} onClose={() => setEditGoal(null)} />
      )}
    </>
  );
};

// =========================================================
// X. APP WRAPPER
// =========================================================
const App = () => (
  <FinanceProvider>
    <MockExternalProvider>
      <GoalsProvider>
        <HomeGoalTracker />
      </GoalsProvider>
    </MockExternalProvider>
  </FinanceProvider>
);

export default App;
